import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Renders volume-anomaly alerts. Pass `alerts` (array). When `slug` is given,
// only that token's alerts are shown (used inside a single report).
// When `collapsible` is set, shows a toggle button + popup overlay so the
// surrounding list isn't pushed down.
export default function MarketAlertBanner({ alerts, slug, collapsible, kinds }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  if (!Array.isArray(alerts) || alerts.length === 0) return null;
  let list = slug ? alerts.filter(a => a.slug === slug) : alerts;
  if (kinds) list = list.filter(a => kinds.includes(a.kind || 'daily'));
  if (list.length === 0) return null;

  const items = (
    <div style={s.items}>
        {list.map((a, i) => {
          const spike = a.type === 'spike';
          const color = spike ? '#dc2626' : '#2563eb';
          const bg = spike ? 'rgba(220,38,38,0.07)' : 'rgba(37,99,235,0.07)';
          return (
            <div key={a._id || i} style={{ ...s.item, background: bg, borderColor: color + '33' }}>
              <span style={{ ...s.itemIcon, color }}>{spike ? '📈' : '📉'}</span>
              <div style={s.itemBody}>
                <div style={s.itemTop}>
                  <span style={{ ...s.itemName, color }}>
                    {a.name || a.slug} {a.ticker ? `(${a.ticker})` : ''}
                  </span>
                  {a.kind === 'intraday' && (
                    <span style={s.liveTag}><span style={s.liveDot} />{t('marketAlert.live')}</span>
                  )}
                  <span style={{ ...s.badge, color, borderColor: color + '55', background: color + '14' }}>
                    {spike ? t('marketAlert.spike') : t('marketAlert.drop')} · {a.ratio ? `${a.ratio.toFixed(1)}×` : ''}
                  </span>
                </div>
                <p style={s.itemMsg}>{a.message}</p>
                {a.date && (
                  <span style={s.itemMeta}>
                    {new Date(a.date).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'vi-VN')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );

  // Collapsible: toggle button + absolutely-positioned popup (doesn't push list)
  if (collapsible) {
    return (
      <div ref={wrapRef} style={s.collapseWrap}>
        <button style={s.toggleBtn} onClick={() => setOpen(o => !o)}>
          <span style={s.headerIcon}>⚠️</span>
          <span style={s.toggleText}>{t('marketAlert.title')}</span>
          <span style={s.count}>{list.length}</span>
          <span style={s.caret}>{open ? '▲' : '▼'}</span>
        </button>
        {open && (
          <div style={s.popup}>
            {items}
          </div>
        )}
      </div>
    );
  }

  // Expanded (default — used inside a single report)
  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <span style={s.headerIcon}>⚠️</span>
        <span style={s.headerText}>{t('marketAlert.title')}</span>
        <span style={s.count}>{list.length}</span>
      </div>
      {items}
    </div>
  );
}

const s = {
  wrap: { marginBottom: 16, animation: 'fadeIn 0.3s ease' },

  collapseWrap: { position: 'relative', marginBottom: 16 },
  toggleBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
    background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.3)',
    borderRadius: 10, padding: '8px 14px', fontFamily: 'var(--font)',
  },
  toggleText: { fontSize: 13, fontWeight: 700, color: 'var(--text)' },
  caret: { fontSize: 9, color: 'var(--text-muted)' },
  popup: {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50,
    width: 'min(560px, 92vw)', maxHeight: 420, overflowY: 'auto',
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
    padding: 12, boxShadow: '0 12px 32px rgba(31,88,164,0.18)', animation: 'fadeIn 0.2s ease',
  },

  header: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  headerIcon: { fontSize: 16 },
  headerText: { fontSize: 13, fontWeight: 800, color: 'var(--text)', letterSpacing: 0.2 },
  count: {
    fontSize: 11, fontWeight: 700, color: '#dc2626', background: 'rgba(220,38,38,0.12)',
    borderRadius: 20, padding: '1px 9px',
  },
  items: { display: 'flex', flexDirection: 'column', gap: 8 },
  item: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    border: '1px solid', borderRadius: 12, padding: '12px 14px', minWidth: 0,
  },
  itemIcon: { fontSize: 18, flexShrink: 0, lineHeight: 1.2 },
  itemBody: { flex: 1, minWidth: 0 },
  itemTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 },
  itemName: { fontSize: 13, fontWeight: 700, wordBreak: 'break-word', overflowWrap: 'break-word' },
  badge: { fontSize: 10, fontWeight: 700, border: '1px solid', borderRadius: 8, padding: '2px 8px', whiteSpace: 'nowrap' },
  liveTag: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9, fontWeight: 700, color: '#dc2626', background: 'rgba(220,38,38,0.1)', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.4 },
  liveDot: { width: 6, height: 6, borderRadius: '50%', background: '#dc2626', display: 'inline-block', animation: 'pulse 1.4s ease-in-out infinite' },
  itemMsg: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.55, margin: '2px 0 4px', wordBreak: 'break-word', overflowWrap: 'break-word' },
  itemMeta: { fontSize: 10, color: 'var(--text-dim)', textTransform: 'capitalize' },
};
